import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import {
  Instagram, Users, TrendingUp, BarChart3, Plus, RefreshCw, Trash2,
  ExternalLink, CheckCircle2, AlertCircle, Loader2, Heart, MessageCircle,
  Play, Eye, X, Flame, Activity, Edit3, Save, Calendar, Filter, ArrowUpDown, ExternalLink as ExternalLinkIcon, ChevronDown, Hash, Settings as SettingsIcon, Settings2, Download, Volume2, VolumeX
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { ModelStatsGrid, ModelStatsDetailModal } from './JGMProModelStats';
import { ReferentesConfigModal } from './JGMProReferentesConfig';

const API = `${BACKEND_URL}/api`;

const formatNumber = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString('es-CO');
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const igProxy = (url) => {
  if (!url) return null;
  return `${API}/jgmpro/img-proxy?url=${encodeURIComponent(url)}`;
};

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
    const w = Math.floor(d / 7);
    if (w < 4) return `${w} week${w > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    return `${mo}mo ago`;
  } catch { return ''; }
};

// Trending score → multiplier label (e.g. 1543 → "1.5x", 154321 → "154x")
const trendMultiplier = (score) => {
  if (!score || score < 100) return '1x';
  if (score < 10000) return `${Math.round(score / 100)}x`;
  return `${Math.round(score / 1000)}x`;
};

const StatCard = ({ icon: Icon, label, value, accent = 'amber' }) => {
  const colors = {
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/30 text-amber-400',
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400',
    emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 text-emerald-400',
    pink: 'from-pink-500/20 to-pink-600/5 border-pink-500/30 text-pink-400',
    violet: 'from-violet-500/20 to-violet-600/5 border-violet-500/30 text-violet-400',
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${colors[accent]} p-5`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">{label}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <div className={`p-3 rounded-xl bg-white/5 ${colors[accent].split(' ').pop()}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const JGMProPanel = () => {
  const [view, setView] = useState('overview'); // overview | accounts | trending
  const [dashboard, setDashboard] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);

  // Trending (discovery feed)
  const [trendSort, setTrendSort] = useState('trending');
  // Nicho / hashtag para búsqueda a demanda en Trending
  const [trendHashtag, setTrendHashtag] = useState('');
  const [trendHashtagInput, setTrendHashtagInput] = useState('');
  // Fuente del feed de Trending: 'auto' (discovery diario) | 'referentes' (videos de referentes)
  const [trendSource, setTrendSource] = useState('auto');
  const [showReferentesConfig, setShowReferentesConfig] = useState(false);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [discoveryStatus, setDiscoveryStatus] = useState(null);
  const [refreshingDiscovery, setRefreshingDiscovery] = useState(false);

  // Accounts sub-view: list of accounts or "my reels"
  const [accountsSubView, setAccountsSubView] = useState('list'); // 'list' | 'reels'
  const [myReels, setMyReels] = useState([]);
  // Modal para el detalle de estadísticas de una modelo (velas + días)
  const [statsModal, setStatsModal] = useState(null); // {id, name} | null

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, accRes, statusRes, discRes] = await Promise.all([
        axios.get(`${API}/jgmpro/dashboard`),
        axios.get(`${API}/jgmpro/accounts`),
        axios.get(`${API}/jgmpro/api-status`).catch(() => ({ data: null })),
        axios.get(`${API}/jgmpro/discovery/status`).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setAccounts(accRes.data.accounts || []);
      setApiStatus(statusRes.data);
      setDiscoveryStatus(discRes.data);
    } catch (err) {
      console.error('JGM PRO load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrending = useCallback(async () => {
    setTrendingLoading(true);
    try {
      let endpoint = `${API}/jgmpro/trending`;
      const params = { sort: trendSort, limit: 60 };
      if (trendSource === 'referentes') {
        // Feed directo de los posts cacheados de los referentes
        endpoint = `${API}/jgmpro/inspiration-posts`;
      } else if (trendHashtag && trendHashtag.trim()) {
        params.hashtag = trendHashtag.trim();
      }
      const res = await axios.get(endpoint, { params });
      setTrending(res.data.posts || []);
    } catch (err) {
      console.error('Trending load error:', err);
      setTrending([]);
    } finally {
      setTrendingLoading(false);
    }
  }, [trendSort, trendHashtag, trendSource]);

  const loadMyReels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/jgmpro/my-reels`, {
        params: { sort: trendSort, limit: 60 },
      });
      setMyReels(res.data.posts || []);
    } catch (err) {
      console.error('My reels load error:', err);
    }
  }, [trendSort]);

  const handleDiscoveryRefresh = async () => {
    setRefreshingDiscovery(true);
    try {
      await axios.post(`${API}/jgmpro/discovery/run`);
      // Poll for status; reload trending after ~30s
      setTimeout(async () => {
        await loadTrending();
        const dr = await axios.get(`${API}/jgmpro/discovery/status`).catch(() => null);
        if (dr) setDiscoveryStatus(dr.data);
        setRefreshingDiscovery(false);
      }, 30000);
    } catch (err) {
      alert('Error al iniciar discovery: ' + (err?.response?.data?.detail || ''));
      setRefreshingDiscovery(false);
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load trending when entering trending tab or filters change
  useEffect(() => {
    if (view === 'trending') {
      loadTrending();
    }
  }, [view, trendSort, loadTrending]);

  // Load my reels when entering accounts->reels subview
  useEffect(() => {
    if (view === 'accounts' && accountsSubView === 'reels') {
      loadMyReels();
    }
  }, [view, accountsSubView, trendSort, loadMyReels]);

  return (
    <div className="space-y-6" data-testid="jgmpro-panel">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-pink-500 via-fuchsia-500 to-amber-500">
            <Instagram className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">JGM PRO</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-gray-400">Centro de operaciones Instagram</p>
              {apiStatus && (
                <span
                  data-testid="jgmpro-api-status"
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full border ${
                    apiStatus.ok
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                      : 'bg-red-500/10 border-red-500/30 text-red-300'
                  }`}
                  title={apiStatus.message}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${apiStatus.ok ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                  Scrape Creators {apiStatus.ok ? 'conectada' : 'sin conexión'}
                  {apiStatus.ok && apiStatus.credits != null && (
                    <span data-testid="jgmpro-credit-counter" className="ml-1 px-1.5 py-0.5 rounded-full bg-white/10 text-white text-[10px] font-bold">
                      {apiStatus.credits.toLocaleString()} créditos
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              if (!window.confirm('¿Auditar todas las cuentas ahora? Consume 1 crédito por cuenta activa.')) return;
              try {
                await axios.post(`${API}/jgmpro/audit/run`);
                alert('Auditoría iniciada en background. Los datos aparecerán en 1-3 minutos.');
                // Refrescar api-status para actualizar créditos
                setTimeout(loadAll, 15000);
              } catch (e) {
                alert('Error al iniciar la auditoría: ' + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="jgmpro-audit-all-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium transition-all text-sm"
            title="Ejecuta audit manual de TODAS las cuentas activas ahora (1 crédito c/u)"
          >
            <RefreshCw className="w-4 h-4" />
            Auditar todas ahora
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="jgmpro-add-account-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 text-white font-medium transition-all shadow-lg shadow-pink-500/20 text-sm"
          >
            <Plus className="w-4 h-4" />
            Agregar cuenta
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white/5 border border-white/10 rounded-xl w-fit">
        {[
          { id: 'overview', label: 'Resumen', icon: BarChart3 },
          { id: 'accounts', label: 'Cuentas', icon: Users },
          { id: 'trending', label: 'Trending', icon: Flame },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              data-testid={`jgmpro-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                view === t.id
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && !dashboard && (
        <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
      )}

      {/* OVERVIEW */}
      {view === 'overview' && dashboard && (
        <div className="space-y-6">
          {/* Resumen compacto (una sola línea) */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300">
              <span className="text-gray-500">Cuentas activas:</span>{' '}
              <span className="text-white font-semibold">{dashboard.active_accounts}</span> / {dashboard.total_accounts}
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300">
              <span className="text-gray-500">Total followers:</span>{' '}
              <span className="text-white font-semibold">{formatNumber(dashboard.total_followers)}</span>
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300">
              <span className="text-gray-500">Engagement avg:</span>{' '}
              <span className="text-white font-semibold">{dashboard.avg_engagement}%</span>
            </span>
          </div>

          {/* MÉTRICAS SEPARADAS POR CUENTA — 1 card por cuenta con sus propias métricas */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-pink-400" />
              <h3 className="text-white font-semibold text-base">Métricas por cuenta</h3>
              <span className="text-[10px] text-gray-500">
                Cada cuenta con sus propios followers, posts y engagement
              </span>
            </div>
            {accounts.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl text-gray-500 text-sm">
                Aún no tienes cuentas registradas. Usa &quot;Agregar cuenta&quot;.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {accounts
                  .slice()
                  .sort((a, b) => (b.followers || 0) - (a.followers || 0))
                  .map((a) => (
                    <button
                      key={a.id}
                      data-testid={`jgmpro-account-metric-card-${a.id}`}
                      onClick={() => { setSelectedAccount(a); setShowDetailModal(true); }}
                      className="text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:border-pink-500/40 p-4 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        {a.profile_pic_url ? (
                          <img
                            src={`${API}/jgmpro/img-proxy?url=${encodeURIComponent(a.profile_pic_url)}`}
                            alt={a.username}
                            className="w-11 h-11 rounded-full object-cover border border-white/10"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-zinc-800 flex items-center justify-center text-lg">
                            👤
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-semibold text-sm truncate">
                            @{a.username}
                          </div>
                          <div className="text-[10px] text-gray-500 flex items-center gap-1">
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                a.status === 'active'
                                  ? 'bg-emerald-400'
                                  : a.status === 'paused'
                                  ? 'bg-amber-400'
                                  : 'bg-red-400'
                              }`}
                            />
                            {a.status || 'active'}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div className="rounded-lg bg-black/30 p-2">
                          <div className="text-[9px] uppercase tracking-wider text-gray-500">
                            Followers
                          </div>
                          <div className="text-white font-bold text-sm mt-0.5">
                            {formatNumber(a.followers || 0)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-black/30 p-2">
                          <div className="text-[9px] uppercase tracking-wider text-gray-500">
                            Posts
                          </div>
                          <div className="text-white font-bold text-sm mt-0.5">
                            {formatNumber(a.media_count || 0)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-black/30 p-2">
                          <div className="text-[9px] uppercase tracking-wider text-gray-500">
                            Engagement
                          </div>
                          <div className="text-white font-bold text-sm mt-0.5">
                            {a.engagement_rate ?? 0}%
                          </div>
                        </div>
                      </div>

                      {a.followees != null && (
                        <div className="mt-2 text-[10px] text-gray-500 flex items-center justify-between">
                          <span>Siguiendo: {formatNumber(a.followees || 0)}</span>
                          {a.last_audit_at && (
                            <span title={a.last_audit_at}>
                              📅 {new Date(a.last_audit_at).toLocaleDateString('es-CO')}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* NUEVO: Estadísticas por modelo (velas + click para detalle) */}
          <div className="rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-500/5 to-purple-500/5 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-pink-400" />
                  Estadísticas por modelo
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Crecimiento diario (velas) de cada modelo. Click en una card para ver el detalle día a día.
                </p>
              </div>
            </div>
            <ModelStatsGrid onOpenModel={(mid, mname) => setStatsModal({ id: mid, name: mname })} />
          </div>

          {/* Followers per account - BAR CHART */}
          {accounts.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Followers por cuenta</h3>
                <span className="text-xs text-gray-500">Top {Math.min(10, accounts.length)}</span>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...accounts].sort((a,b) => (b.followers||0) - (a.followers||0)).slice(0, 10).map(a => ({
                      name: '@' + a.username.substring(0, 12),
                      followers: a.followers || 0,
                      engagement: a.engagement_rate || 0,
                    }))}
                    margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                    <XAxis dataKey="name" stroke="#737373" fontSize={11} angle={-25} textAnchor="end" />
                    <YAxis stroke="#737373" fontSize={11} tickFormatter={formatNumber} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(v) => formatNumber(v)}
                      cursor={{ fill: 'rgba(236,72,153,0.08)' }}
                    />
                    <Bar dataKey="followers" radius={[6, 6, 0, 0]}>
                      {[...accounts].sort((a,b) => (b.followers||0) - (a.followers||0)).slice(0, 10).map((_, i) => (
                        <Cell key={i} fill={`url(#grad-followers-${i % 3})`} />
                      ))}
                    </Bar>
                    <defs>
                      <linearGradient id="grad-followers-0" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#be185d" />
                      </linearGradient>
                      <linearGradient id="grad-followers-1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a855f7" />
                        <stop offset="100%" stopColor="#7e22ce" />
                      </linearGradient>
                      <linearGradient id="grad-followers-2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" />
                        <stop offset="100%" stopColor="#b45309" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Timeline (line) */}
            {dashboard.timeline.length > 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> Crecimiento total (30 días)</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dashboard.timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis dataKey="date" stroke="#737373" fontSize={11} />
                      <YAxis stroke="#737373" fontSize={11} tickFormatter={formatNumber} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: 8 }}
                        formatter={(v) => formatNumber(v)}
                      />
                      <Line type="monotone" dataKey="total_followers" stroke="#ec4899" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> Crecimiento total</h3>
                <div className="h-56 flex items-center justify-center text-gray-500 text-sm">
                  Sin datos históricos aún
                </div>
              </div>
            )}

            {/* Engagement per account (bars) */}
            {accounts.length > 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Engagement rate por cuenta</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[...accounts].sort((a,b) => (b.engagement_rate||0) - (a.engagement_rate||0)).slice(0, 10).map(a => ({
                        name: '@' + a.username.substring(0, 10),
                        rate: a.engagement_rate || 0,
                      }))}
                      margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                      <XAxis dataKey="name" stroke="#737373" fontSize={11} angle={-25} textAnchor="end" />
                      <YAxis stroke="#737373" fontSize={11} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: 8 }}
                        formatter={(v) => `${v}%`}
                        cursor={{ fill: 'rgba(251,191,36,0.08)' }}
                      />
                      <Bar dataKey="rate" radius={[6, 6, 0, 0]} fill="url(#grad-engagement)" />
                      <defs>
                        <linearGradient id="grad-engagement" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fbbf24" />
                          <stop offset="100%" stopColor="#d97706" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Engagement rate</h3>
                <div className="h-56 flex items-center justify-center text-gray-500 text-sm">
                  Agrega cuentas para ver métricas
                </div>
              </div>
            )}
          </div>

          {/* Top accounts lists */}
          <div className="grid lg:grid-cols-2 gap-4">
            <TopList title="Top followers" data={dashboard.top_by_followers} metric="followers" />
            <TopList title="Top engagement" data={dashboard.top_by_engagement} metric="engagement_rate" suffix="%" />
          </div>

          {/* Daily counter — global followers per day */}
          <DailyCounterGlobal timeline={dashboard.timeline} />
        </div>
      )}

      {/* ACCOUNTS */}
      {view === 'accounts' && (
        <div className="space-y-5">
          {/* Sub-tabs */}
          <div className="flex gap-1 p-1 bg-white/5 border border-white/10 rounded-xl w-fit">
            {[
              { id: 'list', label: 'Lista de cuentas', icon: Users },
              { id: 'reels', label: 'Reels de mis modelos', icon: Play },
            ].map(s => {
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setAccountsSubView(s.id)}
                  data-testid={`jgmpro-accounts-sub-${s.id}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    accountsSubView === s.id
                      ? 'bg-white/10 text-white shadow-sm'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {s.label}
                </button>
              );
            })}
          </div>

          {accountsSubView === 'list' && (
            <AccountsList
              accounts={accounts}
              onSelect={(a) => { setSelectedAccount(a); setShowDetailModal(true); }}
              onRefreshAll={loadAll}
            />
          )}

          {accountsSubView === 'reels' && (
            <MyReelsGrid
              posts={myReels}
              sortMode={trendSort}
              setSortMode={setTrendSort}
            />
          )}
        </div>
      )}

      {/* TRENDING */}
      {view === 'trending' && (
        <div className="space-y-4">
          {/* Source selector: discovery automático vs videos de referentes */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex bg-white/5 border border-white/10 rounded-xl p-1">
              <button
                onClick={() => { setTrendSource('auto'); setTrendHashtag(''); setTrendHashtagInput(''); }}
                data-testid="jgmpro-trend-source-auto"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  trendSource === 'auto'
                    ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                🔍 Discovery automático
              </button>
              <button
                onClick={() => setTrendSource('referentes')}
                data-testid="jgmpro-trend-source-referentes"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  trendSource === 'referentes'
                    ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                ✨ Videos de mis referentes
              </button>
            </div>
            <button
              onClick={() => setShowReferentesConfig(true)}
              data-testid="jgmpro-open-referentes-config"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 text-xs font-medium transition"
              title="Configurar referentes"
            >
              <Settings2 className="w-3.5 h-3.5" />
              Configurar referentes
            </button>
          </div>

          {/* Buscador por nicho — solo aplica en modo Discovery */}
          {trendSource === 'auto' && (
            <div className="bg-gradient-to-br from-fuchsia-500/5 to-orange-500/5 border border-fuchsia-500/20 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Hash className="w-4 h-4 text-fuchsia-400" />
                <h3 className="text-white font-semibold text-sm">Buscar por nicho / hashtag</h3>
                <span className="ml-auto text-[10px] text-gray-500">1 crédito por búsqueda</span>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Escribe cualquier hashtag (sin #) para traer al momento los reels ganadores de ese nicho.
                Deja vacío para ver los resultados del scan automático diario.
              </p>
              <form
                onSubmit={(e) => { e.preventDefault(); setTrendHashtag(trendHashtagInput); }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  data-testid="jgmpro-trending-hashtag-input"
                  value={trendHashtagInput}
                  onChange={(e) => setTrendHashtagInput(e.target.value.replace(/^#/, '').toLowerCase())}
                  placeholder="ej: fitnesslatina, colombia, viajes, moda..."
                  className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                />
                <button
                  type="submit"
                  data-testid="jgmpro-trending-hashtag-search"
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-sm font-medium hover:opacity-90"
                >
                  Buscar
                </button>
                {trendHashtag && (
                  <button
                    type="button"
                    onClick={() => { setTrendHashtag(''); setTrendHashtagInput(''); }}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-sm hover:bg-white/10"
                  >
                    Limpiar
                  </button>
                )}
              </form>
              {trendHashtag && (
                <div className="mt-3 text-xs text-fuchsia-300">
                  Mostrando resultados en vivo para <span className="font-bold">#{trendHashtag}</span>
                </div>
              )}
            </div>
          )}

          {trendSource === 'referentes' && (
            <div className="rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-3 text-xs text-gray-300 flex items-center gap-2">
              <span>✨</span>
              <span>
                Mostrando los últimos videos de tus <strong>referentes configuradas</strong>. Los datos se refrescan diariamente.
                Para actualizar ahora, entra a <strong>Configurar referentes → Refrescar todas</strong>.
              </span>
            </div>
          )}

          <TrendingGrid
            posts={trending}
            loading={trendingLoading || refreshingDiscovery}
            sortMode={trendSort}
            setSortMode={setTrendSort}
            onRefresh={handleDiscoveryRefresh}
            lastUpdated={discoveryStatus?.last_discovered_at}
          />
        </div>
      )}

      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); loadAll(); }}
        />
      )}

      {showDetailModal && selectedAccount && (
        <AccountDetailModal
          accountId={selectedAccount.id}
          onClose={() => { setShowDetailModal(false); setSelectedAccount(null); }}
          onChanged={loadAll}
        />
      )}

      {/* Detalle de estadísticas por modelo — velas + día a día */}
      {statsModal && (
        <ModelStatsDetailModal
          modeloId={statsModal.id}
          modeloName={statsModal.name}
          onClose={() => setStatsModal(null)}
        />
      )}

      {/* Configuración de referentes (inspiration creators) */}
      <ReferentesConfigModal
        open={showReferentesConfig}
        onClose={() => setShowReferentesConfig(false)}
        onChanged={() => { if (trendSource === 'referentes') loadTrending(); }}
      />
    </div>
  );
};

const DailyCounterGlobal = ({ timeline }) => {
  if (!timeline || timeline.length === 0) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4" /> Contador diario (global)
        </h3>
        <p className="text-sm text-gray-500">Sin datos históricos aún. El contador se actualiza con cada auditoría diaria.</p>
      </div>
    );
  }
  // Sort desc by date and compute deltas (immutable - don't mutate timeline props)
  const sorted = [...timeline].sort((a, b) => b.date.localeCompare(a.date));
  const rows = sorted.map((r, i) => ({
    ...r,
    delta: sorted[i + 1] ? r.total_followers - sorted[i + 1].total_followers : 0,
  }));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Contador diario (todas las cuentas)
        </h3>
        <span className="text-xs text-gray-500">{rows.length} día{rows.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-gray-400 text-xs uppercase tracking-wider sticky top-0 backdrop-blur-sm">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Fecha</th>
              <th className="text-right px-5 py-3 font-medium">Followers totales</th>
              <th className="text-right px-5 py-3 font-medium">Δ vs día anterior</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r, i) => (
              <tr key={r.date} className="hover:bg-white/[0.03]">
                <td className="px-5 py-3 text-gray-200">
                  {new Date(r.date + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                  {i === 0 && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Hoy</span>}
                </td>
                <td className="px-5 py-3 text-right font-semibold text-white tabular-nums">
                  {formatNumber(r.total_followers)}
                </td>
                <td className={`px-5 py-3 text-right tabular-nums font-medium ${
                  r.delta > 0 ? 'text-emerald-400' : r.delta < 0 ? 'text-red-400' : 'text-gray-500'
                }`}>
                  {r.delta === 0 ? '—' : (r.delta > 0 ? '+' : '') + formatNumber(r.delta)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TopList = ({ title, data, metric, suffix = '' }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
    <h3 className="text-sm font-medium text-gray-300 mb-3">{title}</h3>
    {data.length === 0 ? (
      <p className="text-sm text-gray-500">Sin datos aún</p>
    ) : (
      <ul className="space-y-2">
        {data.map((a, i) => (
          <li key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
            <span className="text-xs font-bold text-gray-500 w-5">{i + 1}</span>
            {a.profile_pic_url && (
              <img src={igProxy(a.profile_pic_url)} alt={a.username} className="w-8 h-8 rounded-full object-cover" loading="lazy" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">@{a.username}</p>
              {a.full_name && <p className="text-xs text-gray-500 truncate">{a.full_name}</p>}
            </div>
            <span className="text-sm font-semibold text-white">
              {metric === 'engagement_rate' ? `${a[metric] || 0}` : formatNumber(a[metric])}{suffix}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AccountsList = ({ accounts, onSelect, onRefreshAll }) => {
  if (accounts.length === 0) {
    return (
      <div className="py-20 text-center rounded-2xl border border-white/10 border-dashed bg-white/[0.02]">
        <Instagram className="w-12 h-12 mx-auto text-gray-500 mb-3" />
        <p className="text-gray-400 mb-1">No hay cuentas trackeadas</p>
        <p className="text-sm text-gray-500">Agrega la primera cuenta de Instagram para empezar</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jgmpro-accounts-grid">
      {accounts.map(a => (
        <button
          key={a.id}
          onClick={() => onSelect(a)}
          data-testid={`jgmpro-account-card-${a.username}`}
          className="text-left group rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all p-5"
        >
          <div className="flex items-start gap-3 mb-4">
            {a.profile_pic_url ? (
              <img src={igProxy(a.profile_pic_url)} alt={a.username} className="w-12 h-12 rounded-full object-cover ring-2 ring-pink-500/30" loading="lazy" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-white font-bold">
                {a.username[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate flex items-center gap-1">
                @{a.username}
                {a.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
              </p>
              {a.full_name && <p className="text-xs text-gray-400 truncate">{a.full_name}</p>}
              {a.modelo_name && <p className="text-xs text-pink-400 truncate mt-0.5">→ {a.modelo_name}</p>}
            </div>
            {a.status === 'error' ? (
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" title={a.last_audit_error} />
            ) : (
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] uppercase text-gray-500">Seguidores</p>
              <p className="text-sm font-bold text-white">{formatNumber(a.followers)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500">Posts</p>
              <p className="text-sm font-bold text-white">{formatNumber(a.media_count)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500">Engagement</p>
              <p className="text-sm font-bold text-amber-400">{a.engagement_rate || 0}%</p>
            </div>
          </div>

          {a.tags && a.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-white/5">
              {a.tags.slice(0, 4).map((t, i) => (
                <span key={i} className="px-2 py-0.5 text-[10px] rounded-full bg-pink-500/10 text-pink-300">{t}</span>
              ))}
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

const TrendingGrid = ({ posts, loading, sortMode, setSortMode, onRefresh, lastUpdated }) => {
  return (
    <div className="space-y-5">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 via-fuchsia-500/10 to-amber-500/10 border border-pink-500/20">
        <div className="p-2 rounded-lg bg-pink-500/20 flex-shrink-0">
          <Flame className="w-5 h-5 text-pink-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Reels ganadores del nicho — discovery automático</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Escaneamos Instagram buscando contenido bien posicionado similar al de tus referentes. Pasa el cursor sobre un reel para reproducirlo.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              data-testid="jgmpro-trending-sort"
              className="appearance-none pl-9 pr-9 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white hover:bg-white/[0.08] cursor-pointer focus:outline-none"
            >
              <option value="trending" className="bg-zinc-900">Trending</option>
              <option value="views" className="bg-zinc-900">Most Views</option>
              <option value="likes" className="bg-zinc-900">Most Likes</option>
              <option value="recent" className="bg-zinc-900">Most Recent</option>
            </select>
            <ArrowUpDown className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          {lastUpdated && (
            <span className="text-xs text-gray-500 inline-flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Último escaneo: {formatRelative(lastUpdated)}
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          data-testid="jgmpro-trending-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/[0.08]"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Buscar nuevos
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
      ) : posts.length === 0 ? (
        <div className="py-20 text-center rounded-2xl border border-white/10 border-dashed bg-white/[0.02]">
          <Flame className="w-12 h-12 mx-auto text-gray-500 mb-3" />
          <p className="text-gray-400 mb-1">Aún no hay reels descubiertos</p>
          <p className="text-sm text-gray-500">El sistema está escaneando Instagram. Vuelve en 1-2 minutos.</p>
        </div>
      ) : (
        <ReelsGrid posts={posts} testidPrefix="jgmpro-trending-card" />
      )}
    </div>
  );
};

const MyReelsGrid = ({ posts, sortMode, setSortMode }) => {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-400">
          Reels publicados por las cuentas que trackeas — métricas reales actualizadas con cada auditoría.
        </p>
        <div className="relative flex-shrink-0">
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            data-testid="jgmpro-myreels-sort"
            className="appearance-none pl-9 pr-9 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white hover:bg-white/[0.08] cursor-pointer focus:outline-none"
          >
            <option value="trending" className="bg-zinc-900">Best engagement</option>
            <option value="views" className="bg-zinc-900">Most Views</option>
            <option value="likes" className="bg-zinc-900">Most Likes</option>
            <option value="recent" className="bg-zinc-900">Most Recent</option>
          </select>
          <ArrowUpDown className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="py-20 text-center rounded-2xl border border-white/10 border-dashed bg-white/[0.02]">
          <Play className="w-12 h-12 mx-auto text-gray-500 mb-3" />
          <p className="text-gray-400 mb-1">Sin reels aún</p>
          <p className="text-sm text-gray-500">Los reels aparecerán tras la próxima auditoría de tus cuentas</p>
        </div>
      ) : (
        <ReelsGrid posts={posts} testidPrefix="jgmpro-myreel-card" />
      )}
    </div>
  );
};

// Reusable reel grid (used by both Trending and MyReels)
const ReelsGrid = ({ posts, testidPrefix }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="jgmpro-reels-grid">
    {posts.map(p => (
      <ReelCard key={(p.id || p.shortcode) + (p.username || '')} post={p} testid={`${testidPrefix}-${p.shortcode}`} />
    ))}
  </div>
);

const ReelCard = ({ post: p, testid }) => {
  const videoRef = React.useRef(null);
  const [hovering, setHovering] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const [downloading, setDownloading] = React.useState(false);

  const onEnter = () => {
    setHovering(true);
    const v = videoRef.current;
    if (v && p.video_url) {
      v.muted = muted;
      v.play().catch(() => {});
    }
  };
  const onLeave = () => {
    setHovering(false);
    const v = videoRef.current;
    if (v) {
      try { v.pause(); v.currentTime = 0; } catch { /* ignore */ }
    }
  };

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMuted(m => {
      const next = !m;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!p.video_url || downloading) return;
    setDownloading(true);
    try {
      const proxied = igProxy(p.video_url);
      const res = await fetch(proxied);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${p.username || 'reel'}_${p.shortcode || 'video'}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Download error:', err);
      alert('No se pudo descargar el reel');
    } finally {
      setDownloading(false);
    }
  };

  const proxiedVideo = p.video_url ? igProxy(p.video_url) : null;
  const proxiedThumb = p.thumbnail_url ? igProxy(p.thumbnail_url) : null;

  return (
    <a
      href={p.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="group relative aspect-[9/16] rounded-xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-pink-400/50 transition-all"
    >
      {/* Thumbnail (always visible base) */}
      {proxiedThumb ? (
        <img src={proxiedThumb} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-gray-600">
          <Instagram className="w-10 h-10" />
        </div>
      )}

      {/* Video (overlays thumbnail when hovering and playing) */}
      {proxiedVideo && (
        <video
          ref={videoRef}
          src={proxiedVideo}
          loop
          playsInline
          muted={muted}
          preload="none"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
            hovering ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid={`${testid}-video`}
        />
      )}

      {/* Top overlay: date + action buttons (mute, download) */}
      <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-2 flex items-start justify-between gap-1 z-10">
        {p.posted_at ? (
          <div className="inline-flex items-center gap-1 px-1.5 py-1 rounded-md bg-black/60 backdrop-blur-sm text-white text-[10px]">
            <Calendar className="w-3 h-3" />
            {formatRelative(p.posted_at)}
          </div>
        ) : <span />}
        <div className="flex gap-1">
          {p.video_url && (
            <button
              onClick={toggleMute}
              data-testid={`${testid}-mute`}
              title={muted ? 'Activar sonido' : 'Silenciar'}
              className="p-1.5 rounded-md bg-black/60 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/80 transition-colors"
            >
              {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
            </button>
          )}
          {p.video_url && (
            <button
              onClick={handleDownload}
              data-testid={`${testid}-download`}
              title="Descargar reel"
              className="p-1.5 rounded-md bg-black/60 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/80 transition-colors disabled:opacity-50"
              disabled={downloading}
            >
              {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            </button>
          )}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard?.writeText(p.url); }}
            data-testid={`${testid}-copy`}
            title="Copiar link"
            className="p-1.5 rounded-md bg-black/60 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/80 transition-colors"
          >
            <ExternalLinkIcon className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Bottom overlay: stats */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-2.5 space-y-1.5 z-10">
        <div className="flex items-center justify-between text-white text-[11px] font-medium">
          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
            <TrendingUp className="w-3 h-3" />
            <span>{trendMultiplier(p.score)}</span>
          </div>
          {p.video_view_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <Play className="w-3 h-3 fill-current" /> {formatNumber(p.video_view_count)}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Heart className="w-3 h-3" /> {formatNumber(p.likes)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/10 backdrop-blur-sm border border-white/10 text-white/90 text-[11px] truncate min-w-0">
            {p.user_is_verified && <CheckCircle2 className="w-3 h-3 text-blue-400 flex-shrink-0" />}
            <span className="truncate">@{p.username}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-white text-[11px]">
            <MessageCircle className="w-3 h-3" /> {formatNumber(p.comments)}
          </span>
        </div>
      </div>
    </a>
  );
};

const HashtagManagerModal = ({ initial, onClose, onSaved }) => {
  const [tags, setTags] = useState(initial || []);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const cleaned = input.trim().replace(/^#/, '').toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (cleaned && !tags.includes(cleaned)) {
      setTags([...tags, cleaned]);
    }
    setInput('');
  };

  const removeTag = (t) => setTags(tags.filter(x => x !== t));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/jgmpro/hashtags`, { hashtags: tags });
      onSaved(res.data.hashtags || []);
    } catch (err) {
      alert('Error al guardar hashtags');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-zinc-900 border border-white/10 rounded-2xl p-6 shadow-2xl" data-testid="jgmpro-hashtag-manager">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Hash className="w-5 h-5 text-pink-400" />
            Gestionar hashtags
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          Agrega hashtags relevantes a tu nicho. El sistema buscará los <strong className="text-white">reels ganadores</strong> de cada uno para inspirar contenido nuevo.
        </p>

        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">#</span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              placeholder="hashtag (sin #)"
              data-testid="jgmpro-hashtag-input"
              className="w-full pl-7 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-pink-400/50 text-sm"
            />
          </div>
          <button
            onClick={addTag}
            data-testid="jgmpro-hashtag-add"
            className="px-4 py-2 rounded-lg bg-pink-500 hover:bg-pink-400 text-white text-sm font-medium"
          >
            Agregar
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-5 min-h-[44px] p-2 rounded-lg bg-black/30 border border-white/5">
          {tags.length === 0 ? (
            <p className="text-xs text-gray-500 italic p-1">Sin hashtags todavía</p>
          ) : tags.map(t => (
            <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-pink-500/15 border border-pink-400/30 text-pink-200">
              <Hash className="w-3 h-3" />
              {t}
              <button onClick={() => removeTag(t)} className="hover:text-white ml-1"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="jgmpro-hashtag-save"
          className="w-full py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>
    </div>
  );
};

const AddAccountModal = ({ onClose, onCreated }) => {
  const [username, setUsername] = useState('');
  const [tagsStr, setTagsStr] = useState('');
  const [notes, setNotes] = useState('');
  const [models, setModels] = useState([]);
  const [modeloId, setModeloId] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualFollowers, setManualFollowers] = useState('');
  const [manualPosts, setManualPosts] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    axios.get(`${API}/models`).then(r => setModels(r.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setWarning('');
    try {
      const res = await axios.post(`${API}/jgmpro/accounts`, {
        username: username.trim(),
        modelo_id: modeloId || null,
        tags: tagsStr.split(',').map(t => t.trim()).filter(Boolean),
        notes: notes.trim() || null,
        manual_mode: manualMode,
        manual_followers: manualFollowers ? parseInt(manualFollowers, 10) : 0,
        manual_media_count: manualPosts ? parseInt(manualPosts, 10) : 0,
      });
      if (res.data.scrape_error) {
        setWarning(`Cuenta creada en modo manual. Scraping no disponible: ${res.data.scrape_error}`);
        setTimeout(() => { onCreated(); }, 2200);
      } else {
        onCreated();
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo agregar la cuenta');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-zinc-900 border border-white/10 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="jgmpro-add-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Instagram className="w-5 h-5 text-pink-400" />
            Agregar cuenta de Instagram
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Username</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                data-testid="jgmpro-username-input"
                className="w-full pl-8 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-pink-400/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Modelo vinculada (opcional)</label>
            <select
              value={modeloId}
              onChange={(e) => setModeloId(e.target.value)}
              data-testid="jgmpro-modelo-select"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-pink-400/50"
            >
              <option value="">— Sin vincular —</option>
              {models.map(m => <option key={m.id} value={m.id} className="bg-zinc-900">{m.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Tags (separados por coma)</label>
            <input
              type="text"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              placeholder="vip, principal, latina"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-pink-400/50"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-pink-400/50 resize-none"
            />
          </div>

          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <input
              type="checkbox"
              id="manual-mode"
              checked={manualMode}
              onChange={(e) => setManualMode(e.target.checked)}
              data-testid="jgmpro-manual-mode-checkbox"
              className="w-4 h-4 accent-pink-500"
            />
            <label htmlFor="manual-mode" className="text-xs text-amber-200 cursor-pointer flex-1">
              <strong>Modo manual</strong> — ingresar datos a mano (útil si IG bloquea el scraping)
            </label>
          </div>

          {manualMode && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Seguidores</label>
                <input
                  type="number"
                  min="0"
                  value={manualFollowers}
                  onChange={(e) => setManualFollowers(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-pink-400/50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Posts</label>
                <input
                  type="number"
                  min="0"
                  value={manualPosts}
                  onChange={(e) => setManualPosts(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-pink-400/50"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
          )}

          {warning && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">{warning}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="jgmpro-submit-add-button"
            className="w-full py-3 rounded-lg bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 text-white font-semibold disabled:opacity-50 transition-all"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {manualMode ? 'Guardando...' : 'Validando con Instagram...'}</span>
            ) : (manualMode ? 'Agregar (manual)' : 'Agregar y validar')}
          </button>
        </form>
      </div>
    </div>
  );
};

const AccountDetailModal = ({ accountId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingStats, setEditingStats] = useState(false);
  const [editFollowers, setEditFollowers] = useState('');
  const [editPosts, setEditPosts] = useState('');
  const [editEngagement, setEditEngagement] = useState('');
  const [savingStats, setSavingStats] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jgmpro/accounts/${accountId}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/jgmpro/accounts/${accountId}/refresh`);
      await load();
      onChanged?.();
    } catch (err) {
      alert('Error al refrescar: ' + (err?.response?.data?.detail || 'desconocido'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta cuenta y todas sus métricas históricas?')) return;
    try {
      await axios.delete(`${API}/jgmpro/accounts/${accountId}`);
      onChanged?.();
      onClose();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const startEditStats = () => {
    setEditFollowers(data.account.followers || '');
    setEditPosts(data.account.media_count || '');
    setEditEngagement(data.account.engagement_rate || '');
    setEditingStats(true);
  };

  const handleSaveStats = async () => {
    setSavingStats(true);
    try {
      await axios.put(`${API}/jgmpro/accounts/${accountId}`, {
        manual_followers: editFollowers === '' ? null : parseInt(editFollowers, 10),
        manual_media_count: editPosts === '' ? null : parseInt(editPosts, 10),
        manual_engagement_rate: editEngagement === '' ? null : parseFloat(editEngagement),
      });
      setEditingStats(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert('Error al guardar: ' + (err?.response?.data?.detail || 'desconocido'));
    } finally {
      setSavingStats(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl" data-testid="jgmpro-detail-modal">
        {loading || !data ? (
          <div className="p-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
        ) : (
          <>
            <div className="sticky top-0 bg-zinc-900/95 backdrop-blur-sm border-b border-white/10 p-5 flex items-start justify-between gap-4 z-10">
              <div className="flex items-start gap-4 min-w-0">
                {data.account.profile_pic_url ? (
                  <img src={igProxy(data.account.profile_pic_url)} alt="" className="w-16 h-16 rounded-full ring-2 ring-pink-500/40 object-cover" loading="lazy" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-2xl font-bold text-white">
                    {data.account.username[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    @{data.account.username}
                    {data.account.is_verified && <CheckCircle2 className="w-5 h-5 text-blue-400" />}
                  </h3>
                  {data.account.full_name && <p className="text-sm text-gray-400">{data.account.full_name}</p>}
                  {data.account.modelo_name && <p className="text-xs text-pink-400 mt-0.5">Modelo: {data.account.modelo_name}</p>}
                  <a href={`https://instagram.com/${data.account.username}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline mt-1">
                    Ver en Instagram <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={startEditStats} data-testid="jgmpro-edit-stats-button" className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300" title="Editar métricas manualmente">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={handleRefresh} disabled={refreshing} data-testid="jgmpro-refresh-account-button" className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={handleDelete} data-testid="jgmpro-delete-account-button" className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-red-500/20 hover:border-red-500/30 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={onClose} className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {data.account.biography && (
                <p className="text-sm text-gray-300 whitespace-pre-line">{data.account.biography}</p>
              )}

              {editingStats && (
                <div className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-4 space-y-3" data-testid="jgmpro-edit-stats-form">
                  <p className="text-sm font-medium text-pink-300 flex items-center gap-2"><Edit3 className="w-4 h-4" /> Actualizar métricas manualmente</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] uppercase text-gray-400 mb-1">Followers</label>
                      <input
                        type="number"
                        min="0"
                        value={editFollowers}
                        onChange={(e) => setEditFollowers(e.target.value)}
                        data-testid="jgmpro-edit-followers-input"
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-pink-400/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase text-gray-400 mb-1">Posts</label>
                      <input
                        type="number"
                        min="0"
                        value={editPosts}
                        onChange={(e) => setEditPosts(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-pink-400/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase text-gray-400 mb-1">Engagement %</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editEngagement}
                        onChange={(e) => setEditEngagement(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-pink-400/50"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditingStats(false)} className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-gray-300 hover:bg-white/10">Cancelar</button>
                    <button onClick={handleSaveStats} disabled={savingStats} data-testid="jgmpro-save-stats-button" className="px-3 py-1.5 text-xs rounded-lg bg-pink-500 hover:bg-pink-400 text-white font-medium disabled:opacity-50 inline-flex items-center gap-1">
                      {savingStats ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Guardar
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Users} label="Followers" value={formatNumber(data.account.followers)} accent="pink" />
                <StatCard icon={Users} label="Siguiendo" value={formatNumber(data.account.followees)} accent="violet" />
                <StatCard icon={BarChart3} label="Posts" value={formatNumber(data.account.media_count)} accent="blue" />
                <StatCard icon={TrendingUp} label="Engagement" value={`${data.account.engagement_rate || 0}%`} accent="amber" />
              </div>

              {(data.growth_7d !== null || data.growth_30d !== null) && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-xs text-gray-400 uppercase">Crecimiento 7d</p>
                    <p className={`text-2xl font-bold ${(data.growth_7d || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(data.growth_7d || 0) >= 0 ? '+' : ''}{formatNumber(data.growth_7d || 0)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-xs text-gray-400 uppercase">Crecimiento 30d</p>
                    <p className={`text-2xl font-bold ${(data.growth_30d || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(data.growth_30d || 0) >= 0 ? '+' : ''}{formatNumber(data.growth_30d || 0)}
                    </p>
                  </div>
                </div>
              )}

              {data.snapshots && data.snapshots.length > 1 && (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Historial de seguidores</h4>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.snapshots}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                        <XAxis dataKey="date" stroke="#737373" fontSize={11} />
                        <YAxis stroke="#737373" fontSize={11} tickFormatter={formatNumber} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: 8 }}
                          formatter={(v) => formatNumber(v)}
                        />
                        <Line type="monotone" dataKey="followers" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Daily counter (per-account) */}
              {data.snapshots && data.snapshots.length > 0 && (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Contador diario
                    </h4>
                    <span className="text-xs text-gray-500">{data.snapshots.length} día{data.snapshots.length !== 1 ? 's' : ''} registrados</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-sm" data-testid="jgmpro-daily-counter">
                      <thead className="bg-white/[0.02] text-gray-400 text-xs uppercase tracking-wider sticky top-0 backdrop-blur-sm">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium">Día</th>
                          <th className="text-right px-4 py-2.5 font-medium">Seguidores</th>
                          <th className="text-right px-4 py-2.5 font-medium">Δ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {[...data.snapshots].sort((a, b) => b.date.localeCompare(a.date)).map((s, i, arr) => {
                          const prev = arr[i + 1];
                          const delta = prev ? (s.followers || 0) - (prev.followers || 0) : 0;
                          return (
                            <tr key={s.date} className="hover:bg-white/[0.03]">
                              <td className="px-4 py-2.5 text-gray-200">
                                {new Date(s.date + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short' })}
                                {i === 0 && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Hoy</span>}
                              </td>
                              <td className="px-4 py-2.5 text-right font-semibold text-white tabular-nums">{formatNumber(s.followers)}</td>
                              <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${
                                delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-gray-500'
                              }`}>
                                {delta === 0 ? '—' : (delta > 0 ? '+' : '') + formatNumber(delta)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {data.posts && data.posts.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Posts recientes</h4>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {data.posts.slice(0, 12).map(p => (
                      <a key={p.id || p.shortcode} href={p.url} target="_blank" rel="noopener noreferrer" className="group relative aspect-square rounded-lg overflow-hidden bg-zinc-800">
                        {p.thumbnail_url ? (
                          <img src={igProxy(p.thumbnail_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-600"><Instagram className="w-6 h-6" /></div>
                        )}
                        {p.is_video && (
                          <Play className="absolute top-1.5 right-1.5 w-3 h-3 text-white drop-shadow" />
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 text-xs text-white">
                          <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{formatNumber(p.likes)}</span>
                          <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{formatNumber(p.comments)}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {data.account.last_audit_at && (
                <p className="text-xs text-gray-500 text-center pt-2 border-t border-white/5">
                  Última auditoría: {formatDate(data.account.last_audit_at)}
                  {data.account.last_audit_error && (
                    <span className="block text-red-400 mt-1">Error: {data.account.last_audit_error}</span>
                  )}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JGMProPanel;
